// RITA-1522 (Nutzungserfassung Phase 3d, Epic RITA-1496) — Rechenregeln der Dichte-Wolke.
//
// Reine Funktionen ohne React und ohne Canvas (Muster: services/usageFormat.ts). Die
// Zeichenfläche ruft sie nur auf; damit bleibt die eigentliche Fachlogik einzeln prüfbar und
// die Canvas-Anbindung dünn — in jsdom gibt es kein getContext('2d').
//
// Zwei Koordinatensysteme: die Antwort rastert in DOKUMENT-Koordinaten (Spalte = 5 % der
// Fensterbreite des Betrachters zum Klickzeitpunkt, Zeile = 64 px ab Seitenanfang, siehe
// readUsageDaily.ts CONTRACT 13). Die Zeichenfläche liegt dagegen fest im Fenster
// (position: fixed). Die Umrechnung ist deshalb immer: Anteil × aktuelle Fensterbreite für x,
// Dokumentzeile minus Scrollstand für y.
import type { UsageClickMapCell } from '../types';

/** Spalten des Rasters — Spiegel von CLICK_MAP_GRID_COLUMNS (readUsageDaily.ts). */
export const HEATMAP_SPALTEN = 20;
/** Höhe einer Rasterzeile in Dokumentpixeln — Spiegel von CLICK_MAP_ROW_HEIGHT_PX. */
export const HEATMAP_ZEILENHOEHE = 64;

/** Rechteck einer Zelle in FENSTER-Koordinaten (Ursprung oben links der Zeichenfläche). */
export interface ZellenRechteck {
  left: number;
  top: number;
  breite: number;
  hoehe: number;
}

export interface ZeichenFenster {
  innerWidth: number;
  scrollY: number;
}

/**
 * Rechteck einer Zelle im Fenster. `top` wird NICHT auf 0 geklemmt: eine Zelle, die halb über
 * dem oberen Rand liegt, gehört zur Hälfte ins Bild — mit einer Kappung rutschte ihr Kreis
 * beim Scrollen an den Rand statt hinauszuwandern.
 */
export const zellenRechteck = (zelle: UsageClickMapCell, fenster: ZeichenFenster): ZellenRechteck => {
  const breiteGesamt = Math.max(0, Number(fenster.innerWidth) || 0);
  const spaltenbreite = breiteGesamt / HEATMAP_SPALTEN;
  return {
    left: zelle.x * spaltenbreite,
    top: zelle.y * HEATMAP_ZEILENHOEHE - (Number(fenster.scrollY) || 0),
    breite: spaltenbreite,
    hoehe: HEATMAP_ZEILENHOEHE,
  };
};

export interface SichtFenster {
  innerHeight: number;
  scrollY: number;
  /**
   * Zusätzlicher Rand in Pixeln über und unter dem Fenster. Der gezeichnete Kreis ist breiter
   * als seine Zelle; ohne Rand poppte die Wolke beim Scrollen an der Kante auf. Der Aufrufer
   * übergibt den Radius, damit hier keine zweite Zahl für dieselbe Größe steht.
   */
  rand?: number;
}

/**
 * Die Zellen, die den sichtbaren Dokumentausschnitt berühren. Alles andere wird gar nicht erst
 * gezeichnet: eine Seite kann tausende Zellen haben, sichtbar sind immer nur ein paar Dutzend.
 */
export const zellenImFenster = (zellen: readonly UsageClickMapCell[], fenster: SichtFenster): UsageClickMapCell[] => {
  const rand = Math.max(0, Number(fenster.rand) || 0);
  const oben = (Number(fenster.scrollY) || 0) - rand;
  const unten = (Number(fenster.scrollY) || 0) + (Number(fenster.innerHeight) || 0) + rand;
  return zellen.filter((zelle) => {
    const anfang = zelle.y * HEATMAP_ZEILENHOEHE;
    return anfang + HEATMAP_ZEILENHOEHE > oben && anfang < unten;
  });
};

/** Farbe und Deckkraft einer Zelle. */
export interface HeatmapFarbe {
  r: number;
  g: number;
  b: number;
  deckkraft: number;
}

/** Stützstellen des Verlaufs: wenig = blau, mittel = gelb, viel = rot (wie im Mockup v2). */
const BLAU = { r: 0, g: 120, b: 255 };
const GELB = { r: 255, g: 200, b: 0 };
const ROT = { r: 255, g: 40, b: 40 };

/** Deckkraft der schwächsten Zelle; sie muss sichtbar bleiben, ohne den Inhalt zu verdecken. */
const DECKKRAFT_MIN = 0.25;
/** Deckkraft der stärksten Zelle — bewusst unter 1: die Oberfläche bleibt lesbar. */
const DECKKRAFT_MAX = 0.8;

const mische = (von: { r: number; g: number; b: number }, bis: { r: number; g: number; b: number }, t: number) => ({
  r: Math.round(von.r + (bis.r - von.r) * t),
  g: Math.round(von.g + (bis.g - von.g) * t),
  b: Math.round(von.b + (bis.b - von.b) * t),
});

/**
 * Farbe und Deckkraft zum Anteil (count / maxCount, 0..1). Werte außerhalb werden geklemmt und
 * NaN gilt als 0 — eine Karte mit genau einer Zelle hat maxCount = count, jede Division bleibt
 * damit im Bereich, aber eine leere Karte darf keine unsinnige Farbe erzeugen.
 */
export const farbeFuer = (anteil: number): HeatmapFarbe => {
  const wert = Number.isFinite(anteil) ? Math.min(1, Math.max(0, Number(anteil))) : 0;
  const farbe = wert <= 0.5 ? mische(BLAU, GELB, wert * 2) : mische(GELB, ROT, (wert - 0.5) * 2);
  return { ...farbe, deckkraft: DECKKRAFT_MIN + (DECKKRAFT_MAX - DECKKRAFT_MIN) * wert };
};
